import {Alert, Box, Card, CardContent, Chip, Stack, Typography} from "@mui/material";
import {BookOpen} from "lucide-react";
import {useMemo} from "react";
import sanitizeHtml from "sanitize-html";
import SchoolChip from "@/components/Fees/SchoolChip.tsx";
import ViewMoreModalLink from "@/components/Fees/ViewMoreModalLink.tsx";
import {type ApiFetch, apiEndpoint} from "@/components/Providers/JWTProvider.tsx";
import {formatter} from "@/Helpers/formatter.ts";

type ReceiptFee = {
    id: string;
    name: string;
    description: string;
    feeType: string;
    school: string | null;
    feeCategory: string | null;
};

type ReceiptPayment = {
    amount: number;
    date: string;
};

type ReceiptAssessedFee = {
    id: string;
    notes: string;
    dateAssessed: string;
    status: string;
    selfAssessed: boolean;
    payment: ReceiptPayment | null;
    fee: ReceiptFee;
};

type ReceiptProgram = {
    id: string;
    programName: string;
};

type ReceiptCourse = {
    id: string;
    courseName: string;
    courseNumber: string;
    courseDescription: string;
    subject: string;
};

type ReceiptCourseSection = {
    id: string;
    sectionNumber: number;
    meetingDateStart: string;
    meetingTimeStart: string | null;
    meetingDateEnd: string | null;
    meetingTimeEnd: string | null;
    meetingDaysOfWeek: string;
    course: ReceiptCourse;
    program: ReceiptProgram;
};

type ReceiptRegistration = {
    id: string;
    status: string;
    courseSection: ReceiptCourseSection;
};

type ReceiptCartItem = {
    id: string;
    type: "fee" | "registration";
    assessedFee: ReceiptAssessedFee | null;
    registration: ReceiptRegistration | null;
};

export type ReceiptCart = {
    id: string;
    updatedAt: string;
    paidCents: number;
    status: string;
    cartItems: ReceiptCartItem[];
};

export const fetchData = async (fetch: ApiFetch, cartId: string) => {
    const url = new URL(`/v1/cart/${cartId}`, apiEndpoint);
    const response = await fetch(url.toString());
    if (!response.ok) {
        return null;
    }

    return (await response.json()) as ReceiptCart;
};

export const DisplayCartReceipt = ({cart}: {cart: ReceiptCart}) => {
    return (
        <>
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: {xs: "visible", md: "auto"},
                }}
            >
                {cart.cartItems.map((cartItem: ReceiptCartItem) => {
                    if (cartItem.assessedFee) {
                        return (
                            <DisplayFeeCartItem cartItem={cartItem.assessedFee} key={cartItem.id} />
                        );
                    }
                    if (cartItem.registration) {
                        return (
                            <DisplayRegistrationCartItem
                                cartItem={cartItem.registration}
                                key={cartItem.id}
                            />
                        );
                    }
                })}
                <Typography variant="h6">
                    Total Paid: {formatter.format(cart.paidCents / 100)}
                </Typography>
                <Typography>Cart ID: {cart.id}</Typography>
            </Box>
        </>
    );
};

const DisplayFeeCartItem = ({cartItem}: {cartItem: ReceiptAssessedFee}) => {
    const feeDescription = useMemo(() => {
        return sanitizeHtml(cartItem.fee.description, {
            allowedTags: [],
        }).substring(0, 120);
    }, [cartItem]);

    return (
        <Card
            variant="outlined"
            sx={{
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                transition: "box-shadow 0.3s ease-in-out",
                "&:hover": {
                    boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
                },
                border: "1px solid rgba(0,0,0,0.12)",
                backgroundColor: "background.paper",
                mb: 2,
                mr: 2,
            }}
        >
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexDirection: {xs: "column", md: "row"},
                    }}
                >
                    <Box sx={{display: "flex", gap: 2, flex: 1, mr: 4}}>
                        <Box sx={{color: "primary"}}>
                            <BookOpen />
                        </Box>
                        <Box sx={{flex: 1}}>
                            <Typography variant="h6" gutterBottom>
                                {cartItem.fee.name}
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                color="text.secondary"
                                gutterBottom
                            >
                                {feeDescription}
                                {feeDescription.length >= 120 && (
                                    <ViewMoreModalLink fee={cartItem.fee} />
                                )}
                            </Typography>

                            {cartItem.notes && (
                                <Alert severity="info" sx={{marginBottom: 1}}>
                                    {cartItem.notes}
                                </Alert>
                            )}
                            <Stack direction="row" spacing={1}>
                                {cartItem.fee.school && <SchoolChip school={cartItem.fee.school} />}
                                {cartItem.fee.feeCategory && (
                                    <Chip
                                        label={cartItem.fee.feeCategory}
                                        size="small"
                                        color={"secondary"}
                                        sx={{height: "24px"}}
                                    />
                                )}
                                {(!cartItem.selfAssessed || cartItem.selfAssessed) && (
                                    <Chip
                                        label={"Optional"}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        sx={{height: "24px"}}
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Box>
                    <Box sx={{minWidth: 160, textAlign: "right", width: {xs: "100%", md: "auto"}}}>
                        {cartItem.payment?.amount && (
                            <Typography>
                                Amount Paid: {formatter.format(cartItem.payment?.amount)}
                            </Typography>
                        )}
                        <Typography>
                            Paid: {new Date(cartItem.payment?.date ?? "").toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const DisplayRegistrationCartItem = ({cartItem}: {cartItem: ReceiptRegistration}) => {
    return <Typography>{cartItem.courseSection.course.courseName}</Typography>;
};
